import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useSignUp, SignUpForm, SignUpFormValues  } from '@auth';
import type { OAuthProvider } from '@auth';
import { trackEvent, tagClaritySession } from '@/shared/analytics';
import { errorMessagesOr } from '@/shared/utils/fieldErrors';
import { trackFormServerErrors } from '@/shared/analytics/formServerErrors';

interface SignUpPageProps {
  onSwitchToSignIn: () => void;
  onOAuth: (provider: OAuthProvider) => void;
  oauthError: string | null;
}

export default function SignUpPage({ onSwitchToSignIn, onOAuth, oauthError }: SignUpPageProps) {
  const intl = useIntl();
  const { mutateAsync: signUpAsync, reset: resetSignUp, isPending, error: signUpError } = useSignUp();

  const fallbackMessage = intl.formatMessage({ id: 'auth.signUp.failed' });

  const serverErrors = useMemo(() => errorMessagesOr(signUpError, fallbackMessage), [signUpError, fallbackMessage]);

  const handleSubmit = async (values: SignUpFormValues) => {
    resetSignUp();

    try {
      await signUpAsync({
        name: values.name,
        email: values.email,
        password: values.password,
        password_confirmation: values.passwordConfirmation,
        country: values.country,
        phone: values.phone,
        terms_accepted: values.termsAccepted,
      });
      trackEvent('sign_up', { method: 'credentials' });
      tagClaritySession('funnel_stage', 'sign_up_complete');
      tagClaritySession('sign_up_method', 'credentials');
    } catch (error) {
      trackFormServerErrors(error, 'sign_up');
    }
  };

  return (
    <SignUpForm
      serverErrors={serverErrors}
      oauthError={oauthError}
      isLoading={isPending}
      onSubmit={handleSubmit}
      onOAuth={onOAuth}
      onSwitchToSignIn={onSwitchToSignIn}
    />
  );
}
